import { IkeGeodesic } from '@ikegps/core-measurement';
import type { LocationData } from '@ikegps/core-api';
import {
	CollectionModelFieldAnchor,
	type CollectionModelFieldLine,
	CollectionModelFieldVector,
} from '@ikegps/core-shared';
import { computed, type IComputedValue, onBecomeUnobserved } from 'mobx';
import { AppStore } from '../../store';

export interface PointCollectionLineInfo {
	field: CollectionModelFieldVector | CollectionModelFieldAnchor;
	target: LocationData;
}

export class IkeTransformer {
	/**
	 * Create a caching function to remember the result  of the transformer,
	 * This will be automatically updated if any observables change inside the transformer.
	 */
	static create<T extends string, R>(transformer: (transformer: T) => R, opts: { name: string }): (obj: T) => R {
		const views: { [id: string]: IComputedValue<R> } = {};

		function createView(source: T): IComputedValue<R> {
			const transformerName = `Transformer-${opts.name}-${source}`;
			const expr = computed(() => transformer(source), { name: transformerName });

			const disposer = onBecomeUnobserved(expr, () => {
				disposer();
				delete views[source];
			});

			return expr;
		}

		return (obj: T) => {
			let existing = views[obj];
			if (existing == null) {
				existing = views[obj] = createView(obj);
			}
			return existing.get();
		};
	}
}

export class PointCollectionCache {
	/** Get a list of collection vectors given the collection revision */
	static getCollectionVectors = IkeTransformer.create<string, PointCollectionLineInfo[]>(
		(revision) => {
			const collection = AppStore.Collection.get(revision);
			if (collection == null) {
				return null;
			}

			const output: PointCollectionLineInfo[] = [];
			collection.forEachField((field) => {
				if (field.value == null) {
					return;
				}

				const collectionLocation = PointCollectionCache.getCollectionLocation(revision);

				if (CollectionModelFieldVector.isVectorField(field)) {
					const { distance, bearing } = field.value;
					const target = IkeGeodesic.projectLocation(collectionLocation, bearing, distance);
					output.push({ field, target });
				}

				if (CollectionModelFieldAnchor.isAnchorField(field)) {
					const { distance, bearing } = field.distanceBearingPitch(collectionLocation);
					const target = IkeGeodesic.projectLocation(collectionLocation, bearing, distance);
					output.push({ field, target });
				}
			});

			return output;
		},
		{ name: 'getCollectionVectors' }
	);

	/** Saved (>=2-point) line fields for a collection. */
	static getCollectionLineFields = IkeTransformer.create<string, CollectionModelFieldLine[]>(
		(revision) => {
			const collection = AppStore.Collection.get(revision);
			if (collection == null) {
				return null;
			}
			return collection.savedLineFields();
		},
		{ name: 'getCollectionLineFields' }
	);

	/** Location is hard to find, so cache the location lookups */
	static getCollectionLocation = IkeTransformer.create<string, LocationData>(
		(revision) => {
			const collection = AppStore.Collection.get(revision);
			if (collection == null) {
				return null;
			}
			// trace();
			return collection.location;
		},
		{ name: 'getCollectionLocation' }
	);
}
